import {
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import {
  isModelConceptRelationship,
  isModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/utils/data';
import { isDetachedKey } from 'src/containers/ProjectDiagram/utils/detachedKey';
import {
  getModelConceptClassValue,
  getOrCreateModelConceptClassKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getModelConceptRelationshipKey } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationship';
import { getModelConceptRelationshipLabelKey } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationshipLabel';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';

export const changeModelConceptRelationshipConnection =
  ({
    key,
    fromIri,
    toIri,
  }: {
    key: string;
    fromIri?: string;
    toIri?: string;
  }) =>
  (model: Model) => {
    const linkData = model.linkDataArray.find(
      (existing) => existing.key === key
    );
    if (!linkData || !isModelConceptRelationship(linkData)) {
      return { linkData: undefined, linkLabelData: undefined };
    }

    const oldFromIri = linkData.from
      ? getModelConceptClassValue(linkData.from)
      : undefined;
    const oldToIri = linkData.to
      ? getModelConceptClassValue(linkData.to)
      : undefined;

    if (oldFromIri === fromIri && oldToIri === toIri) {
      return { linkData: undefined, linkLabelData: undefined };
    }

    const oldFromKey = getOrCreateModelConceptClassKey(oldFromIri);
    const oldToKey = getOrCreateModelConceptClassKey(oldToIri);
    const fromKey = getOrCreateModelConceptClassKey(fromIri);
    const toKey = getOrCreateModelConceptClassKey(toIri);

    const linkLabelKey = linkData.labelKeys[0];
    const linkLabelData = model.nodeDataArray.find(
      (existing) => existing.key === linkLabelKey
    );

    if (!linkLabelData || !isModelConceptRelationshipLabel(linkLabelData)) {
      console.warn(new Error(`couldn't find linkLabel ${linkLabelKey}`));
      return { linkData: undefined, linkLabelData: undefined };
    }

    if (oldFromKey !== fromKey) {
      const newFromKey = isDetachedKey(fromKey) ? undefined : fromKey;
      setDataProperty<ModelConceptRelationship>(model)(
        linkData,
        'from',
        newFromKey
      );
      setDataProperty<ModelConceptRelationshipLabel>(model)(
        linkLabelData,
        'from',
        newFromKey
      );
    }

    if (oldToKey !== toKey) {
      const newToKey = isDetachedKey(toKey) ? undefined : toKey;
      setDataProperty<ModelConceptRelationship>(model)(
        linkData,
        'to',
        newToKey
      );
      setDataProperty<ModelConceptRelationshipLabel>(model)(
        linkLabelData,
        'to',
        newToKey
      );
    }

    const { value } = linkData;
    const newFromIri = getModelConceptClassValue(fromKey);
    const newToIri = getModelConceptClassValue(toKey);

    const newLinkKey = getModelConceptRelationshipKey({
      value,
      fromIri: newFromIri,
      toIri: newToIri,
    });

    const newLinkLabelKey = getModelConceptRelationshipLabelKey({
      value,
      fromIri: newFromIri,
      toIri: newToIri,
    });

    setDataProperty<ModelConceptRelationship>(model)(linkData, 'labelKeys', [
      newLinkLabelKey,
    ]);

    model.setKeyForLinkData(linkData, newLinkKey);
    model.setKeyForNodeData(linkLabelData, newLinkLabelKey);

    return { linkData, linkLabelData };
  };
